import React, { useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import cx from "classnames";
import styles from "./index.module.scss";
import modalStyles from "../Index/securityModals.module.scss";
import { message, Button, Modal, Form, Input, Select } from "antd";
import { useRouter } from "next/router";
import {
  get_fiatPaymentMethodUserList,
  get_fiatListForWithdraw,
  get_fiatPaymentMethodListForWithdraw,
  post_fiatConfigureUserPaymentMethod,
  delete_fiatUserPaymentMethod,
} from "@/api/xt4/payment";
import { waitRes } from "utils/axios";
import { Hooks } from "@xt/xtbase";
import store from "store";
import DeleteIcon from "@/assets/images/delete.png";
import EditIcon from "@/assets/images/payment-method/edit.svg";
import ListDeleteIcon from "@/assets/images/payment-method/delete.svg";
import { getOrigin } from "utils/method";
import { checkIsLegalUrl } from "utils/validation";
import useIsDesktop from "@/hooks/useIsDesktop";
import { useAccountHeaderActions } from "components/AccountShell";
import {
  IconNavPencil,
  IconPmLandmark,
  IconPmPlus,
  IconSecTrash,
} from "@/components/icons/accountIcons";

const { useTranslation } = Hooks;
const { Option } = Select;

interface PaymentMethodItem {
  groupKey: string;
  payName: string;
  realName: string;
  status: number;
  icon?: string;
  accountNumber?: string;
  currency?: string;
  currencyId?: number;
  fields?: Record<string, string>;
  createdTime: string;
  updatedTime: string;
}

type PaymentMethodModalMode = "add" | "edit";

interface FieldOption {
  value: string;
  label: string;
}

interface StandardField {
  key: string;
  name: string;
  isMust: number;
  sort?: number;
  inputType?: string;
  options?: FieldOption[];
  /** list/withdraw 已配置时带回的用户填写值 */
  value?: string;
  pattern?: string | null;
}

/** list/withdraw 下单个渠道（用于 VeePay/Bcpay/PIX 与 fields 兜底） */
interface PaymentMethodRoute {
  payName?: string;
  payEnName?: string;
  fields?: PaymentMethodRouteField[];
  configs?: PaymentMethodRouteField[];
}

interface PaymentMethodGroupOption {
  groupKey: string;
  payName: string;
  icon?: string;
  standardFields?: StandardField[];
  paymentMethods?: PaymentMethodRoute[];
  /** 接口：是否已在本组绑过卡 */
  isConfigured?: boolean;
  /** list/withdraw 已配置组的字段回显值（standardFieldKey -> value） */
  configuredFields?: Record<string, string>;
  currency?: string;
  currencyId?: number;
  accountNumber?: string;
  minAmount?: string;
  maxAmount?: string;
}

/** 接口 list/withdraw 组项内嵌渠道的 fields（与组级 standardFields 二选一或并存） */
interface PaymentMethodRouteField {
  key?: string;
  name?: string;
  columnName?: string;
  columnEnName?: string;
  isMust?: number;
  sort?: number;
  inputType?: string;
  options?: FieldOption[];
  value?: string;
  fieldValue?: string;
  userValue?: string;
  pattern?: string | null;
}

interface FiatCurrency {
  /** 与接口对齐：优先 currencyId，兼容历史 id */
  id: number;
  currency: string;
  displayName: string;
}

function normalizeFiatCurrencyRow(item: Record<string, unknown>): FiatCurrency | null {
  const rawId = item.currencyId ?? item.id;
  const id = typeof rawId === "number" ? rawId : rawId != null ? Number(rawId) : NaN;
  if (Number.isNaN(id)) return null;
  const cur = (item.currency ?? item.coinName ?? "") as string;
  const displayName =
    (item.displayName as string) ||
    (item.currencyName as string) ||
    (typeof cur === "string" && cur ? cur.toUpperCase() : "") ||
    String(id);
  return {
    id,
    currency: typeof cur === "string" ? cur : String(cur),
    displayName,
  };
}

function normalizeFieldOptions(raw: unknown): FieldOption[] {
  if (!Array.isArray(raw)) return [];
  const out: FieldOption[] = [];
  for (const opt of raw) {
    const rec = opt as Record<string, unknown>;
    const value = rec?.value == null ? "" : String(rec.value).trim();
    if (!value) continue;
    const labelRaw = rec?.label == null ? "" : String(rec.label).trim();
    out.push({ value, label: labelRaw || value });
  }
  return out;
}

function mapFormField(raw: StandardField | PaymentMethodRouteField): StandardField {
  const r = raw as PaymentMethodRouteField & StandardField;
  const key = r.key || r.columnEnName || r.columnName || "";
  return {
    key,
    name: r.name || r.columnName || r.columnEnName || key,
    isMust: typeof r.isMust === "number" ? r.isMust : 0,
    sort: r.sort,
    inputType: r.inputType || "",
    options: normalizeFieldOptions(r.options),
    pattern: r.pattern ?? null,
  };
}

function getFormConfigsForGroup(group: PaymentMethodGroupOption): Array<StandardField | PaymentMethodRouteField> {
  const fromTop = Array.isArray(group.standardFields) ? group.standardFields : [];
  if (fromTop.length > 0) return fromTop;
  const methods = Array.isArray(group.paymentMethods) ? group.paymentMethods : [];
  for (const pm of methods) {
    const configs = getMethodConfigs(pm);
    if (configs.length > 0) return configs;
  }
  return [];
}

function isBankCodeFieldKey(key: string | undefined): boolean {
  return String(key || "").toLowerCase() === "bankcode";
}

function isSelectField(field: StandardField): boolean {
  const inputType = String(field.inputType || "").toLowerCase();
  if (inputType === "select") return true;
  return Array.isArray(field.options) && field.options.length > 0;
}

function getFieldOptions(field: StandardField, pixContext: boolean): FieldOption[] {
  const options = field.options || [];
  if (!isBankCodeFieldKey(field.key) || !pixContext) return options;
  const cpf = options.find((opt) => String(opt.value).toUpperCase() === "CPF");
  return cpf ? [cpf] : options.length > 0 ? options : [{ value: "CPF", label: "CPF" }];
}

/** 从组项解析 standardFields：优先组级 standardFields，否则用 paymentMethods 内 fields/configs */
function resolveStandardFieldsFromGroupItem(item: Record<string, unknown>): StandardField[] {
  const groupKey = item.groupKey;
  if (!groupKey || typeof groupKey !== "string") return [];
  const group: PaymentMethodGroupOption = {
    groupKey,
    payName: (item.payName as string) || "",
    standardFields: Array.isArray(item.standardFields) ? (item.standardFields as StandardField[]) : undefined,
    paymentMethods: Array.isArray(item.paymentMethods) ? (item.paymentMethods as PaymentMethodRoute[]) : [],
  };
  return getFormConfigsForGroup(group)
    .sort((a, b) => (a.sort || 999) - (b.sort || 999))
    .map((f) => mapFormField(f));
}

function getGroupDisplayWeight(item: Record<string, unknown>): number {
  const pms = Array.isArray(item.paymentMethods) ? item.paymentMethods : [];
  if (pms.length === 0) return 999;
  const w = (pms[0] as Record<string, unknown>)?.displayWeights;
  return typeof w === "number" ? w : 999;
}

/** 渠道字段 key 对齐组级 standardFieldKey（如 accountNo -> account） */
function normalizeConfiguredFieldKey(key: string | undefined): string {
  const k = String(key || "").trim();
  if (!k) return "";
  const lower = k.toLowerCase();
  if (lower === "accountno" || lower === "accountnumber") return "account";
  return k;
}

function resolveFieldValueFromConfig(raw: Record<string, unknown>): string | undefined {
  const val = raw.value ?? raw.fieldValue ?? raw.userValue ?? raw.columnValue;
  if (val == null) return undefined;
  const s = String(val).trim();
  return s === "" ? undefined : s;
}

/** 从 list/withdraw 组项解析用户已配置字段值，供编辑回显 */
function resolveConfiguredFieldValuesFromWithdrawGroup(item: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {};

  const merge = (rawKey: string | undefined, value: string | undefined) => {
    if (!rawKey || value == null || value === "") return;
    const key = normalizeConfiguredFieldKey(rawKey);
    if (!key) return;
    out[key] = value;
  };

  const mergeFieldMap = (map: unknown) => {
    if (!map || typeof map !== "object" || Array.isArray(map)) return;
    for (const [k, v] of Object.entries(map as Record<string, unknown>)) {
      if (v != null && String(v).trim() !== "") merge(k, String(v).trim());
    }
  };

  const userPaymentMethods = item.userPaymentMethods;
  if (Array.isArray(userPaymentMethods) && userPaymentMethods.length > 0) {
    const configured = userPaymentMethods[0] as Record<string, unknown>;
    mergeFieldMap(configured.standardFields);
    if (configured.accountNumber != null && String(configured.accountNumber).trim() !== "") {
      merge("account", String(configured.accountNumber).trim());
    }
    return out;
  }

  mergeFieldMap(item.fields);

  const stdFields = item.standardFields;
  if (Array.isArray(stdFields)) {
    for (const f of stdFields) {
      const rec = f as Record<string, unknown>;
      const key = (rec.key || rec.columnEnName || rec.columnName) as string | undefined;
      merge(key, resolveFieldValueFromConfig(rec));
    }
  }

  if (item.accountNumber != null && String(item.accountNumber).trim() !== "") {
    merge("account", String(item.accountNumber).trim());
  }

  return out;
}

function resolveWithdrawGroupAccountNumber(item: Record<string, unknown>): string | undefined {
  const userPaymentMethods = item.userPaymentMethods;
  if (Array.isArray(userPaymentMethods) && userPaymentMethods.length > 0) {
    const n = (userPaymentMethods[0] as Record<string, unknown>).accountNumber;
    if (n != null && String(n).trim() !== "") return String(n).trim();
  }
  if (item.accountNumber != null && String(item.accountNumber).trim() !== "") {
    return String(item.accountNumber).trim();
  }
  return undefined;
}

function normalizeWithdrawGroupItem(item: Record<string, unknown>): PaymentMethodGroupOption | null {
  const groupKey = item.groupKey;
  if (!groupKey || typeof groupKey !== "string") return null;
  const standardFields = resolveStandardFieldsFromGroupItem(item);
  const pms = item.paymentMethods;
  return {
    groupKey,
    payName: (item.payName as string) || "",
    icon: item.icon as string | undefined,
    standardFields,
    paymentMethods: Array.isArray(pms) ? (pms as PaymentMethodRoute[]) : [],
    isConfigured: item.isConfigured === true,
    configuredFields: resolveConfiguredFieldValuesFromWithdrawGroup(item),
    currency: item.currency != null ? String(item.currency) : undefined,
    currencyId: typeof item.currencyId === "number" ? item.currencyId : undefined,
    accountNumber: resolveWithdrawGroupAccountNumber(item),
    minAmount: item.minAmount != null ? String(item.minAmount) : undefined,
    maxAmount: item.maxAmount != null ? String(item.maxAmount) : undefined,
  };
}

function isVeePayMethod(method: PaymentMethodRoute | null | undefined): boolean {
  if (!method) return false;
  const name = (method.payEnName || method.payName || "").toLowerCase();
  return name === "veepay" || name.includes("veepay");
}

function isBcpayMethod(method: PaymentMethodRoute | null | undefined): boolean {
  if (!method) return false;
  const name = (method.payEnName || method.payName || "").toLowerCase();
  return name === "bcpay" || name.includes("bcpay");
}

function isPixMethod(method: PaymentMethodRoute | null | undefined): boolean {
  if (!method) return false;
  const name = (method.payEnName || method.payName || "").toLowerCase();
  return name === "pix" || name.includes("pix");
}

function isPixDualBindingGroup(groupMethod: PaymentMethodGroupOption | null): boolean {
  if (!groupMethod) return false;
  const groupName = (groupMethod.payName || "").toLowerCase();
  if (!groupName.includes("pix")) return false;
  const methods = Array.isArray(groupMethod.paymentMethods) ? groupMethod.paymentMethods : [];
  return methods.some((m) => isVeePayMethod(m)) && methods.some((m) => isBcpayMethod(m));
}

/** PIX 渠道：bankCode 固定 CPF，不展示 CNPJ/PHONE/EMAIL（与 web-index 法币提现绑卡一致） */
function isPixWithdrawGroup(groupMethod: PaymentMethodGroupOption | null): boolean {
  if (!groupMethod) return false;
  if (isPixDualBindingGroup(groupMethod)) return true;
  const groupName = (groupMethod.payName || "").toLowerCase();
  if (groupName.includes("pix")) return true;
  const methods = Array.isArray(groupMethod.paymentMethods) ? groupMethod.paymentMethods : [];
  return methods.some((m) => isPixMethod(m));
}

function getMethodConfigs(method: PaymentMethodRoute | null | undefined): PaymentMethodRouteField[] {
  if (!method) return [];
  const m = method as Record<string, unknown>;
  if (Array.isArray(m.configs) && m.configs.length > 0) return m.configs as PaymentMethodRouteField[];
  if (Array.isArray(m.fields) && m.fields.length > 0) return m.fields as PaymentMethodRouteField[];
  return [];
}

function isAccountFieldKey(key: string | undefined): boolean {
  const k = String(key || "").toLowerCase();
  return k === "account";
}

function isCpfFieldKey(key: string | undefined): boolean {
  const k = String(key || "").toLowerCase();
  return k === "cpf" || k === "document";
}

function stripToDigits(value: unknown, maxLen = 11): string {
  return String(value ?? "").replace(/\D/g, "").slice(0, maxLen);
}

/** CPF 展示：231.002.999-00；表单存 11 位纯数字 */
function formatCpfDisplay(value: unknown): string {
  const digits = stripToDigits(value);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
}

function parseFieldPattern(pattern: string | null | undefined): RegExp | null {
  if (!pattern || typeof pattern !== "string") return null;
  try {
    return new RegExp(pattern);
  } catch {
    return null;
  }
}

function normalizeFieldSubmitValue(key: string, value: string): string {
  return isCpfFieldKey(key) ? stripToDigits(value) : value;
}

function buildStandardFieldRules(
  field: StandardField,
  t: (key: string, params?: Record<string, string>) => string
): Array<Record<string, unknown>> {
  const rules: Array<Record<string, unknown>> = [];
  const label = field.name || field.key;

  if (field.isMust === 1) {
    rules.push({ required: true, message: t("paymentMethod.pleaseEnterField", { field: label }) });
  }

  const patternRe = parseFieldPattern(field.pattern);
  if (patternRe) {
    rules.push({
      validator: (_: unknown, value: unknown) => {
        const raw = isCpfFieldKey(field.key) ? stripToDigits(value) : String(value ?? "").trim();
        if (!raw) return Promise.resolve();
        if (!patternRe.test(raw)) {
          return Promise.reject(new Error(t("paymentMethod.fieldFormatInvalid", { field: label })));
        }
        return Promise.resolve();
      },
    });
  }

  return rules;
}

/** Account 与 CPF 同值提交时，弹窗不展示 Account 输入框 */
function shouldHideAccountField(fields: StandardField[]): boolean {
  return fields.some((f) => isAccountFieldKey(f.key)) && fields.some((f) => isCpfFieldKey(f.key));
}

function normalizeAutoBindQueryValue(value: string | string[] | undefined): string {
  if (Array.isArray(value)) return String(value[0] || "").trim();
  return typeof value === "string" ? value.trim() : "";
}

function normalizeGroupKey(value: string | null | undefined): string {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function isSameGroupKey(left: string | null | undefined, right: string | null | undefined): boolean {
  return normalizeGroupKey(left) === normalizeGroupKey(right);
}

function parseCurrencyFromGroupKey(groupKey: string): string | null {
  const parts = groupKey.split("#");
  return parts.length >= 3 && parts[2] ? parts[2].trim() : null;
}

function normalizePaymentMethodItem(raw: Record<string, unknown>): PaymentMethodItem | null {
  const groupKey = raw.groupKey;
  if (!groupKey || typeof groupKey !== "string") return null;
  const fieldsRaw = raw.fields;
  const fields =
    fieldsRaw && typeof fieldsRaw === "object" && !Array.isArray(fieldsRaw)
      ? Object.fromEntries(
          Object.entries(fieldsRaw as Record<string, unknown>).map(([k, v]) => [k, v == null ? "" : String(v)])
        )
      : undefined;
  return {
    groupKey,
    payName: (raw.payName as string) || "",
    realName: (raw.realName as string) || "",
    status: typeof raw.status === "number" ? raw.status : 0,
    icon: raw.icon as string | undefined,
    accountNumber: raw.accountNumber != null ? String(raw.accountNumber) : undefined,
    currency: raw.currency != null ? String(raw.currency) : undefined,
    currencyId: typeof raw.currencyId === "number" ? raw.currencyId : undefined,
    fields,
    createdTime: (raw.createdTime as string) || "",
    updatedTime: (raw.updatedTime as string) || "",
  };
}

const PaymentMethod: React.FC = () => {
  const t = useTranslation();
  const { isDesktop, isReady } = useIsDesktop();
  const isSpikexPc = isReady && isDesktop;
  const router = useRouter();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodItem[]>([]);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [modalMode, setModalMode] = useState<PaymentMethodModalMode>("add");
  const [editingItem, setEditingItem] = useState<PaymentMethodItem | null>(null);
  const [editModalLoading, setEditModalLoading] = useState(false);
  const [fiatCurrencies, setFiatCurrencies] = useState<FiatCurrency[]>([]);
  const [paymentMethodOptions, setPaymentMethodOptions] = useState<PaymentMethodGroupOption[]>([]);
  const [selectedCurrencyId, setSelectedCurrencyId] = useState<number | null>(null);
  const [selectedGroupKey, setSelectedGroupKey] = useState<string | null>(null);
  const [standardFields, setStandardFields] = useState<StandardField[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deletingItem, setDeletingItem] = useState<PaymentMethodItem | null>(null);
  const [deleting, setDeleting] = useState(false);
  const autoBindStartedRef = useRef(false);
  const autoBindDismissedRef = useRef(false);
  const autoBindCurrencyAppliedRef = useRef(false);
  const autoBindGroupAppliedRef = useRef(false);
  const autoBindRedirectedRef = useRef(false);

  const selectedGroupForModal = useMemo(
    () => (selectedGroupKey ? paymentMethodOptions.find((g) => g.groupKey === selectedGroupKey) ?? null : null),
    [selectedGroupKey, paymentMethodOptions]
  );
  const isPixWithdrawContext = useMemo(() => isPixWithdrawGroup(selectedGroupForModal), [selectedGroupForModal]);
  const autoBindGroupKey = useMemo(() => normalizeAutoBindQueryValue(router.query.autoBindGroupKey), [router.query.autoBindGroupKey]);
  const autoBindCurrencyId = useMemo(() => {
    const raw = normalizeAutoBindQueryValue(router.query.autoBindCurrencyId);
    const id = Number(raw);
    return Number.isFinite(id) && id > 0 ? id : null;
  }, [router.query.autoBindCurrencyId]);
  const autoBindBackUrl = useMemo(() => normalizeAutoBindQueryValue(router.query.autoBindBackUrl), [router.query.autoBindBackUrl]);
  const isAutoBindEditMode = useMemo(
    () => normalizeAutoBindQueryValue(router.query.autoBindMode).toLowerCase() === "edit",
    [router.query.autoBindMode]
  );
  const hasAutoBindParams = useMemo(
    () => router.isReady && !!autoBindBackUrl && !!autoBindGroupKey && !!autoBindCurrencyId,
    [router.isReady, autoBindBackUrl, autoBindGroupKey, autoBindCurrencyId]
  );

  const resolveConfigureErrorMessage = (err: unknown, errData: unknown, mode: PaymentMethodModalMode): string => {
    const fallback = mode === "edit" ? t("paymentMethod.failedToEdit") : t("paymentMethod.failedToAdd");
    if (errData && typeof errData === "object" && "mc" in errData && errData.mc) {
      const mc = String((errData as { mc: string }).mc);
      if (mc === "PAYMENT_FIAT_1002" && mode === "edit") {
        return t("paymentMethod.editBlockedByPendingOrder");
      }
      return t(mc, (errData as { ma?: string[] }).ma);
    }
    if (typeof err === "string" && err) return err;
    if (typeof err === "object" && err !== null && "message" in err && (err as { message?: string }).message) {
      return (err as { message: string }).message;
    }
    return fallback;
  };

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  useEffect(() => {
    if (!hasAutoBindParams || autoBindStartedRef.current || autoBindDismissedRef.current) return;
    autoBindStartedRef.current = true;

    void (async () => {
      const methods = await fetchPaymentMethods({ silent: true });

      if (isAutoBindEditMode) {
        const matchedItem = methods.find((p) => isSameGroupKey(p.groupKey, autoBindGroupKey)) || null;
        if (!matchedItem) {
          message.error(t("paymentMethod.failedToLoadEditForm"));
          return;
        }
        await startEditFlow({
          ...matchedItem,
          currencyId: matchedItem.currencyId ?? autoBindCurrencyId ?? undefined,
        });
        return;
      }

      setAddModalVisible(true);
      await fetchFiatList();
    })();
  }, [hasAutoBindParams, isAutoBindEditMode]);

  useEffect(() => {
    if (isAutoBindEditMode) return;
    if (!hasAutoBindParams || !autoBindStartedRef.current || autoBindCurrencyAppliedRef.current) return;
    if (!addModalVisible || !autoBindCurrencyId || fiatCurrencies.length === 0) return;
    const matchedCurrency =
      fiatCurrencies.find((item) => Number(item.id) === Number(autoBindCurrencyId)) ||
      null;
    if (!matchedCurrency) return;
    autoBindCurrencyAppliedRef.current = true;
    form.setFieldsValue({ currencyId: matchedCurrency.id });
    handleCurrencyChange(matchedCurrency.id);
  }, [addModalVisible, autoBindCurrencyId, fiatCurrencies, form, hasAutoBindParams, isAutoBindEditMode]);

  useEffect(() => {
    if (isAutoBindEditMode) return;
    if (!hasAutoBindParams || !autoBindStartedRef.current || autoBindGroupAppliedRef.current) return;
    if (!addModalVisible || !autoBindCurrencyId || selectedCurrencyId !== autoBindCurrencyId || paymentMethodOptions.length === 0) return;
    const matchedGroup = paymentMethodOptions.find((item) => isSameGroupKey(item.groupKey, autoBindGroupKey)) || null;
    if (!matchedGroup) return;
    autoBindGroupAppliedRef.current = true;
    form.setFieldsValue({ groupKey: matchedGroup.groupKey });
    handlePaymentMethodChange(matchedGroup.groupKey);
  }, [addModalVisible, autoBindCurrencyId, autoBindGroupKey, paymentMethodOptions, selectedCurrencyId, form, hasAutoBindParams, isAutoBindEditMode]);

  const fetchPaymentMethods = async (options?: { silent?: boolean }): Promise<PaymentMethodItem[]> => {
    const silent = options?.silent === true;
    try {
      if (!silent) setLoading(true);
      const { data, err } = await waitRes(get_fiatPaymentMethodUserList, {
        params: {
          status: 1,
        },
      });
      if (err) {
        const errorMsg =
          typeof err === "string"
            ? err
            : (typeof err === "object" && err !== null && "message" in err ? (err as any).message : null) || t("paymentMethod.failedToLoad");
        message.error(errorMsg);
        return [];
      }
      const result = data?.result || data?.data || data;
      let items: Record<string, unknown>[] = [];
      if (Array.isArray(result)) {
        items = result;
      } else if (result?.items) {
        items = result.items;
      } else if (result?.list) {
        items = result.list;
      }
      const normalized = items.map((item) => normalizePaymentMethodItem(item)).filter((item): item is PaymentMethodItem => item != null);
      setPaymentMethods(normalized);
      return normalized;
    } catch (error) {
      console.error(t("paymentMethod.errorLoadLog"), error);
      message.error(t("paymentMethod.failedToLoad"));
      return [];
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleAdd = async () => {
    await fetchPaymentMethods({ silent: true });
    setModalMode("add");
    setEditingItem(null);
    setAddModalVisible(true);
    await fetchFiatList();
  };

  const loadFiatList = async (): Promise<FiatCurrency[]> => {
    try {
      const { data, err } = await waitRes(get_fiatListForWithdraw, {
        params: {
          pageIndex: 1,
          pageSize: 100,
        },
      });
      if (err) return [];
      const result = data?.result || data?.data || data;
      let items: FiatCurrency[] = [];
      if (Array.isArray(result)) {
        items = result;
      } else if (result?.items) {
        items = result.items;
      } else if (result?.list) {
        items = result.list;
      }
      const filtered = items
        .filter((item: any) => item.withdrawStatus === 1 && item.isDisplay === 1)
        .map((item: any) => normalizeFiatCurrencyRow(item as Record<string, unknown>))
        .filter((c): c is FiatCurrency => c != null);
      setFiatCurrencies(filtered);
      return filtered;
    } catch (error) {
      console.error(t("paymentMethod.errorFetchFiatLog"), error);
      return [];
    }
  };

  const fetchFiatList = async () => {
    await loadFiatList();
  };

  const handleCurrencyChange = async (currencyId: number) => {
    setSelectedCurrencyId(currencyId);
    setSelectedGroupKey(null);
    setStandardFields([]);
    // resetFields 会清空 currencyId，需写回以保持 Select 展示与所选一致
    form.resetFields();
    form.setFieldsValue({ currencyId });
    await fetchPaymentMethodOptions(currencyId);
  };

  const parseWithdrawGroupOptions = (result: unknown): PaymentMethodGroupOption[] => {
    let items: any[] = [];
    if (Array.isArray(result)) {
      items = result;
    } else if (result && typeof result === "object") {
      const rec = result as Record<string, unknown>;
      if (Array.isArray(rec.items)) items = rec.items;
      else if (Array.isArray(rec.list)) items = rec.list;
    }
    const zipped = items
      .filter((item: any) => !!item?.groupKey)
      .map((item: any) => {
        const raw = item as Record<string, unknown>;
        const opt = normalizeWithdrawGroupItem(raw);
        if (!opt) return null;
        return { opt, w: getGroupDisplayWeight(raw) };
      })
      .filter((x): x is { opt: PaymentMethodGroupOption; w: number } => x != null);
    zipped.sort((a, b) => a.w - b.w);
    return zipped.map((z) => z.opt);
  };

  const WITHDRAW_LIST_QUERY = {
    pageIndex: 1,
    pageSize: 100,
    includeConfigs: true,
  };

  const fetchWithdrawGroupList = async (currencyId: number): Promise<PaymentMethodGroupOption[]> => {
    try {
      const { data, err } = await waitRes(get_fiatPaymentMethodListForWithdraw, {
        params: {
          currencyId,
          ...WITHDRAW_LIST_QUERY,
        },
      });
      if (err) return [];
      const result = data?.result || data?.data || data;
      const options = parseWithdrawGroupOptions(result);
      setPaymentMethodOptions(options);
      return options;
    } catch (error) {
      console.error(t("paymentMethod.errorFetchPaymentMethodLog"), error);
      return [];
    }
  };

  /** 添加流程：分页拉取提现支付方式组 */
  const loadPaymentMethodOptions = async (currencyId: number): Promise<PaymentMethodGroupOption[]> => {
    return fetchWithdrawGroupList(currencyId);
  };

  /** 编辑流程：拉取含 userPaymentMethods.standardFields 的配置 */
  const loadPaymentMethodOptionsForEdit = async (currencyId: number): Promise<PaymentMethodGroupOption[]> => {
    return fetchWithdrawGroupList(currencyId);
  };

  const fetchPaymentMethodOptions = async (currencyId: number) => {
    await loadPaymentMethodOptions(currencyId);
  };

  const applyGroupFormFields = (
    group: PaymentMethodGroupOption,
    options?: { preserveFieldValues?: boolean; initialFieldValues?: Record<string, string>; accountFallback?: string }
  ) => {
    const sorted = getFormConfigsForGroup(group)
      .sort((a, b) => (a.sort || 999) - (b.sort || 999))
      .map((f) => mapFormField(f));
    setStandardFields(sorted);

    if (options?.preserveFieldValues) return;

    const fieldValues: Record<string, unknown> = {};
    const existingFields = options?.initialFieldValues || {};
    const hideAccount = shouldHideAccountField(sorted);
    const accountFallback = options?.accountFallback || "";

    for (const field of sorted) {
      const k = field.key;
      if (!k) continue;
      if (hideAccount && isAccountFieldKey(k)) continue;
      let v = existingFields[k];
      if ((v == null || v === "") && existingFields) {
        const matchedKey = Object.keys(existingFields).find((ek) => ek.toLowerCase() === k.toLowerCase());
        if (matchedKey) v = existingFields[matchedKey];
      }
      if ((v == null || v === "") && accountFallback) {
        if (isAccountFieldKey(k) || isCpfFieldKey(k)) v = accountFallback;
      }
      if (v != null && String(v).trim() !== "") {
        fieldValues[`field_${k}`] = v;
      }
    }

    if (isPixWithdrawGroup(group)) {
      const bcField = sorted.find((f) => isBankCodeFieldKey(f.key));
      if (bcField) {
        const opts = getFieldOptions(bcField, true);
        const cpf = opts.find((opt) => String(opt.value).toUpperCase() === "CPF");
        fieldValues[`field_${bcField.key}`] = fieldValues[`field_${bcField.key}`] || cpf?.value || "CPF";
      }
    }

    const fv = form.getFieldsValue();
    const cleared: Record<string, unknown> = {};
    Object.keys(fv).forEach((k) => {
      if (k.startsWith("field_")) cleared[k] = undefined;
    });
    form.setFieldsValue({ ...cleared, ...fieldValues });
  };

  /** 与 web-index buildFormFieldsFromStandardFields 一致 */
  const handlePaymentMethodChange = (groupKey: string | undefined, options?: { isEdit?: boolean }) => {
    const isEdit = options?.isEdit === true;

    if (!groupKey) {
      setSelectedGroupKey(null);
      setStandardFields([]);
      const fv = form.getFieldsValue();
      const cleared: Record<string, unknown> = {};
      Object.keys(fv).forEach((k) => {
        if (k.startsWith("field_")) cleared[k] = undefined;
      });
      form.setFieldsValue(cleared);
      return;
    }
    if (!isEdit && paymentMethods.some((p) => isSameGroupKey(p.groupKey, groupKey))) {
      message.error(t("paymentMethod.alreadyBound"));
      form.setFieldsValue({ groupKey: undefined });
      setSelectedGroupKey(null);
      setStandardFields([]);
      return;
    }
    setSelectedGroupKey(groupKey);

    const group = paymentMethodOptions.find((item) => isSameGroupKey(item.groupKey, groupKey)) || null;
    if (!group) {
      setStandardFields([]);
      return;
    }

    applyGroupFormFields(group);
  };

  const openEditModal = async (item: PaymentMethodItem, currencyId: number, currencies: FiatCurrency[]) => {
    const options = await loadPaymentMethodOptionsForEdit(currencyId);
    const matchedGroup = options.find((g) => isSameGroupKey(g.groupKey, item.groupKey)) || null;
    if (!matchedGroup) {
      message.error(t("paymentMethod.failedToLoadEditForm"));
      handleModalCancel();
      return;
    }

    const matchedCurrency = currencies.find((c) => Number(c.id) === Number(currencyId)) || null;
    if (!matchedCurrency) {
      message.error(t("paymentMethod.failedToLoadEditForm"));
      handleModalCancel();
      return;
    }

    setSelectedCurrencyId(matchedCurrency.id);
    form.setFieldsValue({ currencyId: matchedCurrency.id });

    setSelectedGroupKey(matchedGroup.groupKey);
    form.setFieldsValue({ groupKey: matchedGroup.groupKey });
    applyGroupFormFields(matchedGroup, {
      initialFieldValues: matchedGroup.configuredFields,
      accountFallback: matchedGroup.accountNumber,
    });
  };

  const startEditFlow = async (item: PaymentMethodItem) => {
    setModalMode("edit");
    setEditingItem(item);
    setAddModalVisible(true);
    setEditModalLoading(true);
    form.resetFields();

    try {
      let currencyId = item.currencyId ?? autoBindCurrencyId ?? undefined;
      let currencies = fiatCurrencies;

      if (currencyId == null || currencies.length === 0) {
        currencies = await loadFiatList();
      }
      if (currencyId == null) {
        const currencyCode = item.currency || parseCurrencyFromGroupKey(item.groupKey);
        currencyId = currencyCode
          ? currencies.find((c) => c.currency?.toUpperCase() === currencyCode.toUpperCase())?.id
          : undefined;
      }

      if (currencyId == null) {
        message.error(t("paymentMethod.failedToLoadEditForm"));
        handleModalCancel();
        return;
      }

      await openEditModal(item, currencyId, currencies);
    } catch (error) {
      console.error(t("paymentMethod.errorEditLog"), error);
      message.error(t("paymentMethod.failedToLoadEditForm"));
      handleModalCancel();
    } finally {
      setEditModalLoading(false);
    }
  };

  const handleEdit = async (item: PaymentMethodItem) => {
    await startEditFlow(item);
  };

  const handleModalCancel = () => {
    if (hasAutoBindParams) {
      autoBindDismissedRef.current = true;
    }
    setAddModalVisible(false);
    setModalMode("add");
    setEditingItem(null);
    setEditModalLoading(false);
    setSelectedCurrencyId(null);
    setSelectedGroupKey(null);
    setStandardFields([]);
    setPaymentMethodOptions([]);
    form.resetFields();
  };

  const handleModalSubmit = async () => {
    const isEdit = modalMode === "edit";
    try {
      await form.validateFields(["currencyId", "groupKey"]);
      if (!selectedGroupKey) {
        message.error(t("paymentMethod.pleaseSelectPaymentMethod"));
        return;
      }
      if (!isEdit && paymentMethods.some((p) => isSameGroupKey(p.groupKey, selectedGroupKey))) {
        message.error(t("paymentMethod.alreadyBound"));
        return;
      }

      const group = paymentMethodOptions.find((g) => isSameGroupKey(g.groupKey, selectedGroupKey));
      if (!group) {
        message.error(t("paymentMethod.pleaseSelectPaymentMethod"));
        return;
      }

      const stdForRequired =
        Array.isArray(group.standardFields) && group.standardFields.length > 0
          ? group.standardFields
          : resolveStandardFieldsFromGroupItem(group as unknown as Record<string, unknown>);
      const requiredKeys = stdForRequired.filter((f) => f.isMust === 1).map((f) => f.key).filter(Boolean);

      const toTrim = (v: unknown) => (v == null ? "" : String(v)).trim();
      const values = await form.validateFields();
      const fields: Record<string, string> = {};
      const hideAccount = shouldHideAccountField(standardFields);
      const cpfField = standardFields.find((f) => isCpfFieldKey(f.key));
      const cpfValue = cpfField?.key ? normalizeFieldSubmitValue(cpfField.key, toTrim(values[`field_${cpfField.key}`])) : "";
      const accountField = standardFields.find((f) => isAccountFieldKey(f.key));
      for (const f of standardFields) {
        const k = f.key;
        if (!k) continue;
        if (hideAccount && isAccountFieldKey(k)) continue;
        let v = normalizeFieldSubmitValue(k, toTrim(values[`field_${k}`]));
        if (isBankCodeFieldKey(k) && isPixWithdrawContext) v = v || "CPF";
        if (v !== "") fields[k] = v;
      }
      if (hideAccount && accountField?.key && cpfValue) {
        fields[accountField.key] = cpfValue;
      }

      const missingRequired = requiredKeys.filter((k) => !fields[k] || toTrim(fields[k]) === "");
      if (missingRequired.length > 0) {
        message.warning(t("paymentMethod.pleaseFillRequiredFields"));
        return;
      }
      if (Object.keys(fields).length === 0) {
        message.warning(t("paymentMethod.pleaseFillAtLeastOneField"));
        return;
      }

      setSubmitting(true);
      const realName = String(store.user.userInfo?.userId ?? "");

      const { err, data: errData } = await waitRes(post_fiatConfigureUserPaymentMethod, {
        data: {
          groupKey: selectedGroupKey,
          realName,
          fields,
        },
      });

      if (err) {
        message.error(resolveConfigureErrorMessage(err, errData, modalMode));
        return;
      }

      message.success(isEdit ? t("paymentMethod.editSuccess") : t("paymentMethod.addSuccess"));
      handleModalCancel();
      await fetchPaymentMethods();
      if (autoBindBackUrl && !autoBindRedirectedRef.current) {
        autoBindRedirectedRef.current = true;
        if (checkIsLegalUrl(autoBindBackUrl, getOrigin("", "no"))) {
          window.location.replace(autoBindBackUrl);
        } else {
          router.replace("/accounts/user/payment-method");
        }
      }
    } catch (error: any) {
      if (error?.errorFields) return;
      console.error(isEdit ? t("paymentMethod.errorEditLog") : t("paymentMethod.errorAddLog"), error);
      message.error(isEdit ? t("paymentMethod.failedToEdit") : t("paymentMethod.failedToAdd"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (item: PaymentMethodItem) => {
    setDeletingItem(item);
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingItem?.groupKey) return;

    try {
      setDeleting(true);
      const { err, data: errData } = await waitRes((config) => delete_fiatUserPaymentMethod(deletingItem.groupKey, config), {});

      if (err) {
        let errorMsg = t("paymentMethod.failedToDelete");
        if (errData && typeof errData === "object" && "mc" in errData && errData.mc) {
          errorMsg = t(errData.mc as string, (errData as { ma?: string[] }).ma);
        } else if (typeof err === "string" && err) {
          errorMsg = err;
        } else if (typeof err === "object" && err !== null && "message" in err && (err as { message?: string }).message) {
          errorMsg = (err as { message: string }).message;
        }
        message.error(errorMsg);
        return;
      }

      message.success(t("paymentMethod.deleteSuccess"));
      setDeleteModalVisible(false);
      setDeletingItem(null);
      await fetchPaymentMethods();
    } catch (error) {
      console.error(t("paymentMethod.errorDeleteLog"), error);
      message.error(t("paymentMethod.failedToDelete"));
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalVisible(false);
    setDeletingItem(null);
  };

  const maskAccountNumber = (value: string): string => {
    if (!value || typeof value !== "string") return "****";
    const len = value.length;
    if (len <= 1) return "*";
    if (len === 2) return "**";
    if (len === 3) return value[0] + "*" + value[2];
    if (len === 4) return value[0] + "**" + value[3];
    if (len === 5) return value.slice(0, 2) + "*" + value.slice(-2);
    if (len === 6) return value.slice(0, 2) + "**" + value.slice(-2);
    return value.slice(0, 3) + "*".repeat(len - 6) + value.slice(-3);
  };

  const formatAccountNumber = (item: PaymentMethodItem) => {
    if (item.accountNumber) return maskAccountNumber(item.accountNumber);
    return "****";
  };

  const formatMethodDetail = (item: PaymentMethodItem) => {
    const account = formatAccountNumber(item);
    const currency = item.currency ? String(item.currency).toUpperCase() : "";
    return [currency, account].filter(Boolean).join(" · ") || account;
  };

  const handleAddRef = useRef(handleAdd);
  handleAddRef.current = handleAdd;

  const headerAddAction = useMemo(() => {
    if (!isSpikexPc) return null;
    return (
      <Button type="primary" className={styles.headerAddBtn} onClick={() => handleAddRef.current()}>
        <IconPmPlus className={styles.headerAddBtnIcon} />
        {t("paymentMethod.addMethod")}
      </Button>
    );
  }, [isSpikexPc, t]);

  const pcSelectProps = isSpikexPc
    ? { className: styles.pmSelect, dropdownClassName: styles.pmSelectDropdown }
    : {};

  useAccountHeaderActions(headerAddAction);

  return (
    <div className={cx(styles["payment-method-wrapper"], isSpikexPc && styles.spikexPc)}>
      <div className={styles["content"]}>
        {!isSpikexPc ? (
          <header className={styles.mobileHeader}>
            <h1 className={styles.mobileTitle}>{t("paymentMethod.pageTitle")}</h1>
            <p className={styles.mobileSubtitle}>{t("paymentMethod.pageSubtitle")}</p>
          </header>
        ) : null}

        {!isSpikexPc ? (
          <Button type="primary" className={styles["add-btn"]} onClick={handleAdd} block>
            {t("paymentMethod.addAddress")}
          </Button>
        ) : null}

        <div className={styles["payment-list"]}>
          {loading ? (
            <div className={styles["loading"]}>{t("paymentMethod.loading")}</div>
          ) : paymentMethods.length === 0 ? (
            <div className={styles["empty"]}>
              {isSpikexPc ? (
                <>
                  <span className={styles.emptyIconWrap}>
                    <IconPmLandmark className={styles.emptyIcon} />
                  </span>
                  <div className={styles.emptyTitle}>{t("paymentMethod.empty")}</div>
                  <div className={styles.emptyDesc}>{t("paymentMethod.emptyDesc")}</div>
                  <Button type="primary" className={styles["add-btn"]} onClick={handleAdd}>
                    <IconPmPlus className={styles.addBtnIcon} />
                    {t("paymentMethod.addMethod")}
                  </Button>
                </>
              ) : (
                t("paymentMethod.empty")
              )}
            </div>
          ) : (
            paymentMethods.map((item) => (
              <div key={item.groupKey} className={styles["payment-item"]}>
                {isSpikexPc ? (
                  <span className={styles.methodChip} aria-hidden>
                    <IconPmLandmark className={styles.methodChipIcon} />
                  </span>
                ) : null}
                <div className={styles["payment-info"]}>
                  <div className={styles["payment-header"]}>
                    {item.icon && !isSpikexPc ? (
                      <img className={styles["payment-icon"]} src={item.icon} alt={item.payName} />
                    ) : null}
                    <div className={styles["payment-name"]}>{item.payName || t("paymentMethod.paymentMethod")}</div>
                  </div>
                  <div className={styles["payment-account"]}>
                    {!isSpikexPc ? <span className={styles["account-label"]}>{t("paymentMethod.accountNumber")}</span> : null}
                    <span className={styles["account-number"]}>
                      {isSpikexPc ? formatMethodDetail(item) : formatAccountNumber(item)}
                    </span>
                  </div>
                </div>
                <div className={styles["payment-actions"]}>
                  <div className={styles["edit-btn"]} onClick={() => handleEdit(item)} title={t("paymentMethod.edit")}>
                    {isSpikexPc ? (
                      <IconNavPencil className={styles.actionIcon} />
                    ) : (
                      <img src={EditIcon} alt="" aria-hidden />
                    )}
                  </div>
                  <div className={styles["delete-btn"]} onClick={() => handleDelete(item)} title={t("paymentMethod.deleteCard")}>
                    {isSpikexPc ? (
                      <IconSecTrash className={styles.actionIcon} />
                    ) : (
                      <img src={ListDeleteIcon} alt="" aria-hidden />
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {isSpikexPc ? <p className={styles.disclaimer}>{t("paymentMethod.disclaimer")}</p> : null}
      </div>

      <Modal
        open={addModalVisible}
        title={modalMode === "edit" ? t("paymentMethod.editTitle") : t("paymentMethod.addTitle")}
        onCancel={handleModalCancel}
        footer={null}
        className={cx(styles["add-payment-modal"], isSpikexPc && modalStyles.spikexModal, isSpikexPc && styles.pcAddModal)}
        width={isSpikexPc ? 440 : 400}
        centered
      >
        {editModalLoading ? (
          <div className={styles["loading-form-hint"]}>{t("paymentMethod.loadingFormFields")}</div>
        ) : (
        <Form form={form} layout="vertical" onFinish={handleModalSubmit}>
          <Form.Item
            label={t("paymentMethod.currency")}
            name="currencyId"
            rules={[{ required: true, message: t("paymentMethod.pleaseSelectCurrency") }]}
          >
            <Select
              placeholder={t("paymentMethod.selectCurrency")}
              onChange={handleCurrencyChange}
              disabled={modalMode === "edit"}
              showSearch
              {...pcSelectProps}
              filterOption={(input, option) => {
                if (!option) return false;
                const label = option.label as unknown;
                const children = option.children as unknown;
                const labelStr = typeof label === "string" ? label : "";
                const childrenStr = typeof children === "string" ? children : "";
                const text = labelStr || childrenStr || "";
                return text.toLowerCase().indexOf(input.toLowerCase()) >= 0;
              }}
            >
              {fiatCurrencies.map((currency) => (
                <Option key={currency.id} value={currency.id}>
                  {currency.displayName || currency.currency}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {selectedCurrencyId && (
            <Form.Item
              label={t("paymentMethod.paymentMethod")}
              name="groupKey"
              rules={[{ required: true, message: t("paymentMethod.pleaseSelectPaymentMethod") }]}
            >
              <Select
                placeholder={t("paymentMethod.selectPaymentMethod")}
                onChange={(value) => handlePaymentMethodChange(value, { isEdit: modalMode === "edit" })}
                disabled={modalMode === "edit"}
                showSearch
                {...pcSelectProps}
                filterOption={(input, option) => {
                  if (!option) return false;
                  const label = typeof option.label === "string" ? option.label : "";
                  const children = typeof option.children === "string" ? option.children : "";
                  const text = label || children || "";
                  return text.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                }}
              >
                {paymentMethodOptions.map((method) => (
                  <Option key={method.groupKey} value={method.groupKey}>
                    {method.payName}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          {selectedGroupKey && standardFields.length > 0 && (
            <>
              {standardFields
                .filter((field) => !(shouldHideAccountField(standardFields) && isAccountFieldKey(field.key)))
                .map((field) => (
                <Form.Item
                  key={field.key}
                  label={field.name || field.key}
                  name={`field_${field.key}`}
                  rules={buildStandardFieldRules(field, t)}
                  {...(isCpfFieldKey(field.key)
                    ? {
                        getValueFromEvent: (e: React.ChangeEvent<HTMLInputElement>) => stripToDigits(e.target.value),
                        getValueProps: (value: unknown) => ({ value: formatCpfDisplay(value) }),
                      }
                    : {})}
                >
                  {isSelectField(field) ? (
                    <Select
                      placeholder={t("paymentMethod.enterValue", { value: field.name || field.key })}
                      {...pcSelectProps}
                    >
                      {getFieldOptions(field, isPixWithdrawContext).map((opt) => (
                        <Option key={opt.value} value={opt.value}>
                          {opt.label}
                        </Option>
                      ))}
                    </Select>
                  ) : (
                    <Input
                      autoComplete="off"
                      className={isSpikexPc ? styles.pmInput : undefined}
                      maxLength={isCpfFieldKey(field.key) ? 14 : undefined}
                      placeholder={t("paymentMethod.enterValue", { value: field.name || field.key })}
                    />
                  )}
                </Form.Item>
              ))}
            </>
          )}

          {selectedGroupKey && standardFields.length === 0 && (
            <div className={styles["loading-form-hint"]}>{t("paymentMethod.loadingFormFields")}</div>
          )}

          <Form.Item>
            <Button type="primary" htmlType="submit" block loading={submitting} className={styles["submit-btn"]}>
              {t("paymentMethod.confirm")}
            </Button>
          </Form.Item>
        </Form>
        )}
      </Modal>

      <Modal
        open={deleteModalVisible}
        title={null}
        onCancel={handleDeleteCancel}
        footer={null}
        className={cx(
          styles["delete-payment-modal"],
          isSpikexPc && modalStyles.spikexModal,
          isSpikexPc && styles.pcDeleteModal
        )}
        width={isSpikexPc ? 400 : 320}
        centered
        closable
        maskClosable
      >
        <div className={styles["delete-modal-content"]}>
          <div className={styles["delete-modal-icon"]}>
            <img src={DeleteIcon} alt="delete" />
          </div>
          <div className={styles["delete-modal-title"]}>{t("paymentMethod.deleteCard")}</div>
          <div className={styles["delete-modal-message"]}>
            {deletingItem &&
              t("paymentMethod.deleteConfirmMessage", {
                name: deletingItem.payName || t("paymentMethod.paymentMethod"),
                number: formatAccountNumber(deletingItem),
              })}
          </div>
          <div className={styles["delete-modal-actions"]}>
            <Button className={styles["delete-cancel-btn"]} onClick={handleDeleteCancel} disabled={deleting}>
              {t("paymentMethod.cancel")}
            </Button>
            <Button className={styles["delete-confirm-btn"]} onClick={handleDeleteConfirm} loading={deleting}>
              {t("paymentMethod.confirm")}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default observer(PaymentMethod);
